import { createRouter, createWebHistory } from 'vue-router'
import { defineAsyncComponent } from 'vue'
import { wechatLogin } from '@/api/wechat'
import store from '@/store'
import { Toast } from 'vant'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ErrorFallback from '@/components/ErrorFallback.vue'

// 创建异步组件包装器，添加错误处理和加载状态
const createAsyncComponent = (importFunc) => {
  return defineAsyncComponent({
    loader: importFunc,
    loadingComponent: LoadingSpinner,
    errorComponent: ErrorFallback,
    delay: 200, // 延迟显示加载组件
    timeout: 10000, // 10秒超时
    suspensible: false,
    onError(error, retry, fail, attempts) {
      console.error(`Component loading failed (attempt ${attempts}):`, error)
      if (attempts <= 3) {
        // 重试最多3次
        retry()
      } else {
        fail()
      }
    }
  })
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: createAsyncComponent(() => import('../views/Home.vue'))
  },
  {
    path: '/news',
    name: 'News',
    component: createAsyncComponent(() => import('../views/News.vue'))
  },
  {
    path: '/category',
    name: 'Category',
    component: createAsyncComponent(() => import('../views/Category.vue'))
  },
  {
    path: '/news/:id',
    name: 'NewsDetail',
    component: createAsyncComponent(() => import('../views/NewsDetail.vue'))
  },
  {
    path: '/profile',
    name: 'Profile',
    component: createAsyncComponent(() => import('../views/Profile.vue'))
  },
  {
    path: '/workflow/publish',
    name: 'WorkflowPublish',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowPublish.vue'))
  },
  {
    path: '/workflow/mine',
    name: 'WorkflowMine',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'mine' }
  },
  {
    path: '/workflow/todo',
    name: 'WorkflowTodo',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'todo' }
  },
  {
    path: '/workflow/done',
    name: 'WorkflowDone',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'done' }
  },
  {
    path: '/workflow/detail/:id',
    name: 'TaskDetail',
    component: createAsyncComponent(() => import('../views/workflow/TaskDetail.vue'))
  },
  {
    path: '/products',
    name: 'Products',
    component: createAsyncComponent(() => import('../views/Products.vue'))
  },
  {
    path: '/product-detail',
    name: 'ProductDetail',
    component: createAsyncComponent(() => import('../views/ProductDetail.vue'))
  },
  {
    path: '/customize-form',
    name: 'CustomizeForm',
    component: createAsyncComponent(() => import('../views/CustomizeForm.vue'))
  },
  {
    path: '/customize-agreement',
    name: 'CustomizeAgreement',
    component: createAsyncComponent(() => import('../views/CustomizeAgreement.vue'))
  },
  {
    path: '/product-customize',
    name: 'ProductCustomize',
    component: createAsyncComponent(() => import('../views/ProductCustomize.vue'))
  },
  {
    path: '/order-confirm',
    name: 'OrderConfirm',
    component: createAsyncComponent(() => import('../views/OrderConfirm.vue'))
  },
  {
    path: '/order-create',
    name: 'OrderCreate',
    component: createAsyncComponent(() => import('../views/OrderCreate.vue'))
  },
  {
    path: '/order-success',
    name: 'OrderSuccess',
    component: createAsyncComponent(() => import('../views/OrderSuccess.vue'))
  },
  {
    path: '/order-list',
    name: 'OrderList',
    component: createAsyncComponent(() => import('../views/OrderList.vue'))
  },
  {
    path: '/order-status',
    name: 'OrderStatus',
    component: createAsyncComponent(() => import('../views/OrderStatus.vue'))
  },
  {
    path: '/pending-payment',
    name: 'PendingPayment',
    component: createAsyncComponent(() => import('../views/PendingPayment.vue'))
  },
  {
    path: '/pending-shipment',
    name: 'PendingShipment',
    component: createAsyncComponent(() => import('../views/PendingShipment.vue'))
  },
  {
    path: '/pending-receive',
    name: 'PendingReceive',
    component: createAsyncComponent(() => import('../views/PendingReceive.vue'))
  },
  {
    path: '/completed-orders',
    name: 'CompletedOrders',
    component: createAsyncComponent(() => import('../views/CompletedOrders.vue'))
  },
  {
    path: '/order-detail',
    name: 'OrderDetail',
    component: createAsyncComponent(() => import('../views/OrderDetail.vue'))
  },
  {
    path: '/logistics-detail',
    name: 'LogisticsDetail',
    component: createAsyncComponent(() => import('../views/LogisticsDetail.vue'))
  },
  {
    path: '/payment',
    name: 'Payment',
    component: createAsyncComponent(() => import('../views/Payment.vue'))
  },
  {
    path: '/mock-login',
    name: 'MockWechatLogin',
    component: createAsyncComponent(() => import('../views/MockWechatLogin.vue'))
  },
  {
    path: '/address-manage',
    name: 'AddressManage',
    component: createAsyncComponent(() => import('../views/AddressManage.vue'))
  },
  {
    path: '/address-edit',
    name: 'AddressEdit',
    component: createAsyncComponent(() => import('../views/AddressEdit.vue'))
  },
  {
    path: '/address-select',
    name: 'AddressSelect',
    component: createAsyncComponent(() => import('../views/AddressSelect.vue'))
  },
  {
    path: '/button-demo',
    name: 'ButtonDemo',
    component: createAsyncComponent(() => import('../views/ButtonDemo.vue'))
  },
  {
    path: '/share',
    name: 'SharePage',
    component: createAsyncComponent(() => import('../views/SharePage.vue'))
  },
  {
    path: '/wechat-entry-test',
    name: 'WechatEntryTest',
    component: createAsyncComponent(() => import('../views/WechatEntryTest.vue'))
  },
  {
    path: '/freight/preview/:id',
    name: 'FreightPreview',
    component: createAsyncComponent(() => import('../views/freight/FreightPreview.vue'))
  },
  {
    path: '/freight/ship-orders/create',
    name: 'FreightShipOrderCreate',
    component: createAsyncComponent(() => import('../views/freight/FreightShipOrderCreate.vue'))
  },
  {
    path: '/freight/create',
    name: 'FreightCreate',
    component: createAsyncComponent(() => import('../views/freight/FreightCreate.vue'))
  },
  {
    path: '/freight/mine',
    name: 'MyFreights',
    component: createAsyncComponent(() => import('../views/freight/MyFreights.vue'))
  },
  {
    path: '/freight/ship-orders/mine',
    name: 'MyShipOrders',
    component: createAsyncComponent(() => import('../views/freight/MyShipOrders.vue'))
  },
  {
    path: '/freight',
    name: 'FreightPreviewQuery',
    component: createAsyncComponent(() => import('../views/freight/FreightPreview.vue'))
  },
  {
    path: '/buyerorder',
    name: 'BuyerOrder',
    component: createAsyncComponent(() => import('../views/BuyerOrderPreview.vue'))
  },

]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 处理微信登录回调
async function handleWechatLoginCallback(code) {
  try {
    console.log('开始处理微信授权回调...')
    
    // 调用后端API进行微信登录
    const response = await wechatLogin(code)
    
    if (response.code === 1 && response.data) {
      const { token, nickname, headimgurl, uid, unionid, openid } = response.data
      
      // 构建用户信息对象
      const userInfo = {
        uid: uid,
        nickname: nickname || '微信用户',
        avatar: headimgurl || '',
        unionid: unionid,
        openid: openid
      }
      
      // 保存到localStorage
      localStorage.setItem('wechat_token', token)
      localStorage.setItem('wechat_user_info', JSON.stringify(userInfo))
      localStorage.setItem('wechat_uid', uid)
      
      // 更新Vuex状态
      store.dispatch('login', {
        user: userInfo,
        token: token
      })
      
      console.log('微信自动登录成功:', userInfo)
      Toast.success('微信登录成功！')
      
      return userInfo
    } else {
      throw new Error(response.msg || '登录失败')
    }
  } catch (error) {
    console.error('微信登录回调处理失败:', error)
    throw error
  }
}

router.beforeEach((to, from, next) => {
  // 检查是否是微信授权回调
  const isWechatCallback = to.query.code && to.query.state
  
  // 注释掉自动微信授权登录逻辑，允许用户正常访问应用
  // 用户可以通过登录页面手动进行微信登录
  /*
  if (isWechat && !isLoggedIn && !isWechatCallback) {
    const appId = process.env.VUE_APP_WECHAT_MP_APP_ID || 'wx7478be49bad3b7e0'
    
    if (appId && appId !== 'wx7478be49bad3b7e0') {
      const state = Math.random().toString(36).substring(2, 15)
      localStorage.setItem('wechat_login_state', state)
      localStorage.setItem('wechat_redirect_path', to.fullPath)
      
      const currentUrl = window.location.origin + window.location.pathname
      const redirectUri = encodeURIComponent(currentUrl)
      const scope = 'snsapi_userinfo'
      const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`
      
      window.location.href = authUrl
      return
    }
  }
  */
  
  // 如果是微信授权回调，处理授权结果
  if (isWechatCallback) {
    // 验证state参数
    const savedState = localStorage.getItem('wechat_login_state')
    if (to.query.state === savedState) {
      // 清除state
      localStorage.removeItem('wechat_login_state')
      
      // 调用后端API处理授权码
      console.log('微信授权回调:', {
        code: to.query.code,
        state: to.query.state
      })
      
      // 异步处理微信登录
      handleWechatLoginCallback(to.query.code)
        .then(() => {
          // 登录成功后跳转
          const redirectPath = localStorage.getItem('wechat_redirect_path') || '/'
          localStorage.removeItem('wechat_redirect_path')
          
          // 使用replace方式跳转，清除URL中的授权参数
          window.location.replace(redirectPath)
        })
        .catch((error) => {
          console.error('微信登录失败:', error)
          Toast.fail('微信登录失败: ' + error.message)
          
          // 登录失败，跳转到首页
          const redirectPath = localStorage.getItem('wechat_redirect_path') || '/'
          localStorage.removeItem('wechat_redirect_path')
          window.location.replace(redirectPath)
        })
      
      // 先跳转到当前页面，避免路由阻塞
      next()
      return
    }
  }
  
  // 移除自动添加时间戳的逻辑，避免干扰分享配置
  // 如果需要缓存更新，应该在具体的跳转位置手动添加时间戳
  next();
});

export default router
